// absensi.service — clock-in (hitung kategori dari shift), clock-out, istirahat.
import { currentUser } from '@core/session';
import { getSetting, saveSetting, absensi, type AbsensiCfg } from '@core/settings';
import { todayISO } from '@domain/format';
import { attKategori, toMinutes, deviceMatch, withinRadius, sedangIstirahat, totalIstirahatMenit, type SesiIstirahat } from '@domain/attendance';
import { deviceToken, deviceFp } from '@core/device';
import { pushNotif } from '@core/notif';
import * as repo from './absensi.repo';

const nowMinutes = (): number => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };

/** Kesalahan luar-radius (view menangkap → minta alasan → retry dgn reason). */
export const LUAR_RADIUS = 'LUAR_RADIUS';

/** Selfie wajib (bila diaktifkan) DAN harus milik sendiri — anti-titip: cegah menautkan foto
 * orang lain ke record sendiri lewat API mentah. Dipakai clock-in & istirahat. */
function guardSelfie(empId: number, selfie: string | null): string | null {
  if (selfie && !selfie.startsWith(`${empId}/`)) throw new Error('Selfie tidak valid (bukan milik Anda).');
  if (absensi().wajibSelfie && !selfie) throw new Error('Selfie wajib. Ambil foto dulu.');
  return selfie;
}

/** true/false di dalam radius outlet; null bila outlet tak berkoordinat atau GPS tak tersedia. */
async function cekRadius(outlet: string | null, lat: number | null, lng: number | null): Promise<boolean | null> {
  if (!outlet || lat == null || lng == null) return null;
  const geo = (await repo.listOutletsGeo()).find((o) => o.kode === outlet);
  if (!geo || geo.lat == null || geo.lng == null) return null;
  return withinRadius(geo.lat, geo.lng, lat, lng, geo.radius);
}

export async function clockIn(shiftId: number, opts?: { lat?: number | null; lng?: number | null; reason?: string; selfie?: string | null }): Promise<string> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  const tgl = todayISO();
  if (await repo.todayAttendance(u.id, tgl)) throw new Error('Anda sudah absen masuk hari ini.');
  const shift = (await repo.listShifts(u.outlet)).find((s) => s.id === shiftId);
  if (!shift) throw new Error('Shift tidak valid.');

  const selfie = guardSelfie(u.id, opts?.selfie ?? null);

  // Device-binding: verifikasi HP terdaftar (proxy token+fp). HP beda → tolak.
  // Resilient pre-0041: bila tabel employee_devices belum ada → device-binding nonaktif (clock-in tetap jalan).
  const token = deviceToken(), fp = deviceFp();
  let dev: repo.DeviceRow | null = null, deviceAvail = true;
  try { dev = await repo.getDevice(u.id); }
  catch (e) { if (!/employee_devices|does not exist|relation/i.test((e as Error).message)) throw e; deviceAvail = false; }
  const m = deviceMatch(dev, token, fp);
  if (deviceAvail && !m.ok) throw new Error('HP ini tidak terdaftar untuk absensi Anda. Hubungi Manager untuk reset perangkat.');

  // Geofence: bila outlet punya koordinat & koordinat diberikan → cek radius; luar radius → wajib alasan → req pending.
  const lat = opts?.lat ?? null, lng = opts?.lng ?? null;
  const inRadius = await cekRadius(u.outlet, lat, lng);
  let reqStatus: string | null = null, reqReason: string | null = null;
  if (inRadius === false) {
    const r = (opts?.reason ?? '').trim();
    if (!r) throw new Error(LUAR_RADIUS);
    reqStatus = 'pending'; reqReason = '[Luar radius] ' + r;
  }

  const cfg = getSetting<{ telatSetengah?: number; telatAlpha?: number }>('absensi', {});
  const kat = attKategori(nowMinutes(), toMinutes(shift.jam_masuk), shift.toleransi, cfg.telatSetengah ?? 30, cfg.telatAlpha ?? 60);
  await repo.clockIn({ emp_id: u.id, outlet: u.outlet, tgl, shift_id: shiftId, masuk_at: new Date().toISOString(), kategori: kat, ket: '', masuk_lat: lat, masuk_lng: lng, in_radius: inRadius, req_status: reqStatus, req_reason: reqReason, selfie });
  // Daftar device bila absen pertama (setelah attendance sukses; PK cegah dobel).
  if (deviceAvail && !m.bound) { try { await repo.registerDevice(u.id, token, fp); } catch { /* balapan/PK/pre-0041 → abaikan */ } }
  // Notif approver bila luar radius (best-effort).
  if (reqStatus === 'pending') { try { await pushNotif('SPV', u.outlet, `📍 ${u.nama} absen di LUAR radius (${tgl}) — perlu approval di Koreksi Kehadiran.`); } catch { /* best-effort */ } }
  return kat;
}

/** Reset device karyawan (O/M) → paksa daftar-ulang HP baru. */
export async function resetDevice(empId: number): Promise<void> { mgr(); await repo.resetDevice(empId); }

export async function clockOut(): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  const tgl = todayISO();
  const rec = await repo.todayAttendance(u.id, tgl);
  if (!rec) throw new Error('Belum absen masuk hari ini.');
  if (rec.keluar_at) throw new Error('Anda sudah absen keluar.');
  await repo.clockOut(u.id, tgl, new Date().toISOString());
}

// ---------- Cuti / Leave ----------
export async function requestLeave(fromTgl: string, toTgl: string, note: string): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  if (!fromTgl || !toTgl) throw new Error('Tanggal mulai & selesai wajib diisi.');
  if (toTgl < fromTgl) throw new Error('Tanggal selesai tidak boleh sebelum tanggal mulai.');
  // RLS write_leave: self (emp_id = auth) → karyawan ajukan cuti sendiri.
  await repo.createLeave({ emp_id: u.id, outlet: u.outlet, from_tgl: fromTgl, to_tgl: toTgl, note: note.trim() });
  try { await pushNotif('SPV', u.outlet, `🌴 ${u.nama} mengajukan cuti/izin ${fromTgl} → ${toTgl} — perlu approval di Audit & Approval → Cuti.`); }
  catch { /* best-effort */ }
}

/** Tiering approval by-outlet (SPV-bawahan belum diport): O/M semua; SPV → Teknisi/CS SE-CABANG & bukan diri. */
export function canApproveAtt(targetRole: string, targetOutlet: string | null, targetEmpId: number): boolean {
  const u = currentUser(); if (!u) return false;
  if (['Owner', 'Manager'].includes(u.role)) return true;
  if (u.role === 'SPV') return ['Teknisi', 'CS'].includes(targetRole) && targetOutlet === u.outlet && targetEmpId !== u.id;
  return false;
}

/** Setujui/tolak cuti — O/M atau SPV se-cabang (Teknisi/CS). Rekam pemutus. */
export async function decideLeave(id: number, ok: boolean): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  const lv = await repo.getLeaveFull(id); if (!lv) throw new Error('Cuti tidak ditemukan.');
  if (!canApproveAtt(lv.role, lv.outlet, lv.emp_id)) throw new Error('Persetujuan cuti hanya Owner/Manager atau SPV cabang (untuk Teknisi/CS).');
  await repo.decideLeave(id, ok ? 'approved' : 'rejected', u.nama);
}

// ---------- Istirahat (migrasi 0133) ----------
// Keluar & kembali sama-sama pakai selfie + lokasi seperti absen masuk. Di luar radius TIDAK
// memblokir — orang harus bisa kembali kerja; posisinya direkam agar terlihat di rekap.
interface IstirahatOpts { lat?: number | null; lng?: number | null; selfie?: string | null }

async function attHariIni(): Promise<{ empId: number; rec: repo.AttRow }> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  const rec = await repo.todayAttendance(u.id, todayISO());
  if (!rec) throw new Error('Belum absen masuk hari ini.');
  if (rec.keluar_at) throw new Error('Anda sudah absen keluar hari ini.');
  return { empId: u.id, rec };
}

/** Mulai istirahat. */
export async function istirahatKeluar(opts?: IstirahatOpts): Promise<void> {
  const { empId, rec } = await attHariIni();
  const sesi = Array.isArray(rec.istirahat) ? rec.istirahat : [];
  if (sedangIstirahat(sesi)) throw new Error('Anda sedang istirahat. Tekan "Kembali Kerja" dulu.');
  const selfie = guardSelfie(empId, opts?.selfie ?? null);
  const lat = opts?.lat ?? null, lng = opts?.lng ?? null;
  const baru: SesiIstirahat = { keluarAt: new Date().toISOString(), keluarLat: lat, keluarLng: lng,
    keluarInRadius: await cekRadius(rec.outlet, lat, lng), keluarSelfie: selfie };
  // Bekukan jatah yang berlaku pada sesi pertama hari itu: mengubah kebijakan besok tak boleh
  // menulis ulang penilaian hari ini (pola yang sama dengan `kategori` saat clock-in).
  if (!sesi.length) baru.jatah = absensi().istirahatMenit;
  sesi.push(baru);
  await repo.simpanIstirahat(rec.id, sesi);
}

/** Selesai istirahat → kembali kerja. Mengembalikan menit istirahat sesi ini. */
export async function istirahatKembali(opts?: IstirahatOpts): Promise<number> {
  const { empId, rec } = await attHariIni();
  const sesi = Array.isArray(rec.istirahat) ? rec.istirahat : [];
  const aktif = sesi[sesi.length - 1];
  if (!sedangIstirahat(sesi) || !aktif) throw new Error('Anda sedang tidak istirahat.');
  const selfie = guardSelfie(empId, opts?.selfie ?? null);
  const lat = opts?.lat ?? null, lng = opts?.lng ?? null;
  aktif.kembaliAt = new Date().toISOString(); aktif.kembaliLat = lat; aktif.kembaliLng = lng;
  aktif.kembaliInRadius = await cekRadius(rec.outlet, lat, lng); aktif.kembaliSelfie = selfie;
  await repo.simpanIstirahat(rec.id, sesi);
  return totalIstirahatMenit([aktif]);
}

// ---------- Koreksi kehadiran (request → decide → cancel), port korrRequest/korrDecide/korrCancel v0.18 ----------
/** Karyawan mengajukan koreksi absensi SENDIRI (keterangan + FOTO wajib). Foto & entry tercatat di korr_log. */
export async function requestKoreksi(attId: number, reason: string, foto: string | null): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  if (!reason.trim()) throw new Error('Keterangan permintaan wajib diisi.');
  if (!foto) throw new Error('Foto bukti wajib untuk permintaan approval.'); // GAP B: wajib foto
  if (!foto.startsWith(`${u.id}/`)) throw new Error('Foto tidak valid (bukan milik Anda).'); // anti-titip
  const rec = await repo.getAttFull(attId); if (!rec) throw new Error('Data absensi tidak ditemukan.');
  if (rec.emp_id !== u.id) throw new Error('Hanya dapat mengajukan koreksi absensi sendiri.');
  const entry: repo.KorrEntry = { action: 'request', by: u.nama, role: u.role, ket: reason.trim(), from: '', to: '', at: new Date().toISOString(), foto };
  await repo.requestKoreksi(attId, reason.trim(), [...(rec.korr_log ?? []), entry]);
  // Beri tahu atasan — tanpa ini permintaan hanya "menunggu" tanpa ada yang tahu, dan karyawan
  // mengira sudah diproses. Target 'SPV' terbaca SPV/Manager/Owner (lihat notif.targetMatches).
  try { await pushNotif('SPV', rec.outlet ?? u.outlet, `🕒 ${u.nama} minta koreksi kehadiran (${rec.tgl}) — perlu approval di Audit & Approval → Koreksi Absensi.`); }
  catch { /* best-effort: notif gagal tak boleh membatalkan pengajuan yang sudah tersimpan */ }
}

/** Approver setujui (→ dianggap Tepat/hadir) atau tolak (→ efektif Alpha). Keterangan wajib + dicatat ke korr_log. */
export async function decideKoreksi(attId: number, approve: boolean, ket: string): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  if (!ket.trim()) throw new Error('Keterangan wajib diisi.');
  const rec = await repo.getAttFull(attId); if (!rec) throw new Error('Data absensi tidak ditemukan.');
  if (!canApproveAtt(rec.role, rec.outlet, rec.emp_id)) throw new Error('Anda tidak berwenang mengoreksi kehadiran karyawan ini.');
  const orig = rec.kategori ?? 'ontime', to = approve ? 'ontime' : 'alpha';
  const entry: repo.KorrEntry = { action: approve ? 'Setujui' : 'Tolak', by: u.nama, role: u.role, ket: ket.trim(), from: orig, to, at: new Date().toISOString() };
  await repo.saveKoreksi(attId, { kat_override: approve ? 'ontime' : null, req_status: approve ? 'approved' : 'rejected', korr_log: [...(rec.korr_log ?? []), entry] });
}

/** Batalkan koreksi — HANYA Owner/Manager → balik ke kategori asli. Keterangan wajib + dicatat. */
export async function cancelKoreksi(attId: number, ket: string): Promise<void> {
  const u = currentUser(); if (!u || !['Owner', 'Manager'].includes(u.role)) throw new Error('Pembatalan koreksi hanya Owner/Manager.');
  if (!ket.trim()) throw new Error('Keterangan wajib diisi.');
  const rec = await repo.getAttFull(attId); if (!rec) throw new Error('Data absensi tidak ditemukan.');
  const orig = rec.kategori ?? 'ontime';
  const from = rec.kat_override ?? (rec.req_status === 'rejected' ? 'alpha' : orig);
  const entry: repo.KorrEntry = { action: 'Batalkan', by: u.nama, role: u.role, ket: ket.trim(), from, to: orig, at: new Date().toISOString() };
  await repo.saveKoreksi(attId, { kat_override: null, req_status: null, korr_log: [...(rec.korr_log ?? []), entry] });
}

// ---------- Pengaturan Absensi: shift CRUD + config (O/M; RLS rw_shifts=is_mgr, write_settings=is_mgr) ----------
const mgr = (): void => { const u = currentUser(); if (!u || !['Owner', 'Manager'].includes(u.role)) throw new Error('Khusus Owner/Manager.'); };
const HHMM = /^([01]?\d|2[0-3]):[0-5]\d$/;
export async function saveShift(id: number, f: repo.ShiftInput): Promise<void> {
  mgr();
  if (!f.nama.trim()) throw new Error('Nama shift wajib diisi.');
  if (!f.outlet) throw new Error('Pilih cabang shift.');
  if (!HHMM.test(f.jam_masuk) || !HHMM.test(f.jam_keluar)) throw new Error('Jam harus format HH:MM.');
  const d: repo.ShiftInput = { outlet: f.outlet, nama: f.nama.trim(), jam_masuk: f.jam_masuk, jam_keluar: f.jam_keluar, toleransi: Math.max(0, f.toleransi || 0) };
  if (id) await repo.updateShift(id, d); else await repo.createShift(d);
}
export async function removeShift(id: number): Promise<void> { mgr(); await repo.deleteShift(id); }

export async function saveAbsConfig(c: AbsensiCfg): Promise<void> {
  mgr();
  if (c.telatAlpha < c.telatSetengah) throw new Error('Ambang alpha harus ≥ ambang ½ hari.');
  // Sebar `c` apa adanya lalu klem yang numerik. Versi lama menyebut field satu per satu, jadi setiap
  // setelan baru diam-diam dibuang saat disimpan (istirahatMenit sempat begitu) — bug yang tak terlihat
  // di layar: form terisi benar, nilainya hilang di jalan.
  await saveSetting('absensi', {
    ...absensi(), ...c,
    telatSetengah: Math.max(0, c.telatSetengah), telatAlpha: Math.max(0, c.telatAlpha),
    reminderMenit: Math.max(0, c.reminderMenit), istirahatMenit: Math.max(0, c.istirahatMenit),
  });
}

// ---------- Geofence + hari kerja per cabang (O/M; RLS outlets write=is_mgr) ----------
export async function saveOutletGeo(kode: string, lat: number | null, lng: number | null, radius: number, hariKerja: number[]): Promise<void> {
  mgr();
  await repo.updateOutletGeo(kode, { lat, lng, radius: Math.max(0, radius || 0), hari_kerja: hariKerja });
}

// ---------- Hari Libur (O/M; RLS write_holidays=is_mgr) ----------
export async function addHoliday(tgl: string, nama: string, outlet: string | null): Promise<void> {
  mgr();
  if (!tgl) throw new Error('Tanggal libur wajib diisi.');
  if (!nama.trim()) throw new Error('Nama libur wajib diisi.');
  await repo.createHoliday({ tgl, nama: nama.trim(), outlet });
}
export async function removeHoliday(id: number): Promise<void> { mgr(); await repo.deleteHoliday(id); }

// ---------- Keanggotaan cabang (O/M) — hanya peran cabang (bukan Owner/Manager/Supplier, port v0.18) ----------
export async function moveMember(id: number, outlet: string | null): Promise<void> {
  mgr();
  const emp = (await repo.listMembers()).find((m) => m.id === id); // listMembers sudah kecualikan cross-branch → guard target
  if (!emp) throw new Error('Karyawan bukan peran cabang (Owner/Manager/Supplier tak dikelola keanggotaan cabang).');
  await repo.setEmployeeOutlet(id, outlet);
}
